import { useQuery } from '@tanstack/react-query';
import { library } from '@cwos/api-client';
import { SkeletonSet } from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { authorityGate, gateOpen } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import { LoadFailure } from '../LoadFailure.js';
import { dependentsSummary, lineageCaveat, type DependentsScope } from './libraryLogic.js';

/**
 * "WHAT DEPENDS ON THIS POLICY" — the question this screen used to answer with
 * an honest absence, and now answers with an honest answer.
 *
 * ===========================================================================
 * WHY THE ABSENCE IS STILL HERE
 * ===========================================================================
 * `RuleDetail`'s old sentence — *"Phibian does not yet record where a rule was
 * used"* — was true of the whole product. Founder ruling 4's migration made it
 * false for exactly ONE consumer: a recommendation files a doctrine snapshot
 * naming the canonical rule VERSION that was in force when the advice was
 * given. Automations, forms, eligibility holds, tasks and the rules engine
 * still carry no column referencing a knowledge row, so for them the sentence
 * is still true and it is still printed, under the answer rather than instead
 * of it.
 *
 * This is the discipline the assessment asks for out loud: a panel reading "3"
 * where the truth is "3 recommendations, and four other consumer classes are
 * not tracked at all" is the four-state collapse on the screen a compliance
 * officer would most trust.
 *
 * ===========================================================================
 * A RULE AND A VERSION OF A RULE ARE DIFFERENT QUESTIONS
 * ===========================================================================
 * The corpus stores one immutable row per version. A snapshot cites the row,
 * not the policy. So "what rests on this version" and "what rests on this rule"
 * diverge the moment somebody supersedes it — and a compliance lead reviewing a
 * change means the first. Both are rendered, separately labelled, and the
 * second only when there is more than one version: printing two identical
 * answers would invite a reader to think they had learned two things.
 *
 * ===========================================================================
 * FIVE STATES ON THE SCREEN, AND THE FIFTH IS THE POINT
 * ===========================================================================
 *   CHECKING    the permission set has not arrived. NOT a refusal, and this is
 *               the failure `docs/intelligence/audit/UNKNOWN-IS-NOT-DENIED-
 *               FRONTEND.md` records seven times over: `has(key) === false`
 *               carries "not held", "not arrived" and "could not be read"
 *               alike. Hence `authorityGate`, never a bare `has(...)`.
 *   UNREADABLE  the permission set could not be read. A retry means something.
 *   REFUSED     a RESOLVED set without the key, or the server's own
 *               `refused_for_scope`. No number, ever.
 *   FAILED      the dependents read itself failed. "We couldn't look just now"
 *               — explicitly not a statement that nothing depends on the rule.
 *   ANSWERED    a list, a measured zero, or a withheld count. `dependentsSummary`
 *               is the one place those three become sentences.
 *
 * The query is NOT fired for a reader the gate has already refused. A refusal
 * we can predict is one to explain, not to provoke — the same choice the
 * Library's approvals tab makes, and it keeps a 403 out of the network log of a
 * screen that already knew the answer.
 */

/** The key that opens the dependents question. Named once. */
const RECOMMENDATION_READ = 'recommendation:read';

/**
 * The wire type, aliased and not re-declared. A second spelling of the four
 * states in the app would be a second place for a fifth to appear.
 */
type Facet = library.RuleDependentsFacet;

export function RuleDependents({ ruleId }: { ruleId: string }) {
  const permissions = usePermissions();
  const gate = authorityGate(permissions, RECOMMENDATION_READ);
  const open = gateOpen(gate);

  const dependents = useQuery({
    queryKey: ['library', 'rule', ruleId, 'dependents'],
    queryFn: () => library.ruleDependents(ruleId),
    enabled: ruleId !== '' && open,
  });

  return (
    <section className="rn-card" data-rule-dependents>
      <h2 className="rn-card-title">What relies on this</h2>

      {open ? null : (
        <GateNotice
          gate={gate}
          what="see which decisions rest on this rule"
          heading="Not shown to you"
          detail={
            `Seeing which decisions rest on this rule needs the ${RECOMMENDATION_READ} ` +
            'permission, which you do not hold. Phibian has not looked, so it has no number ' +
            'to give you — this is not a count of zero.'
          }
          onRetry={() => permissions.refetch()}
        />
      )}

      {open && dependents.isLoading ? <SkeletonSet surface="drawer" rows={3} /> : null}

      {open && dependents.isError ? (
        <LoadFailure
          error={dependents.error}
          message="We couldn’t look up what rests on this rule just now. This is not a statement that nothing does."
          onRetry={() => void dependents.refetch()}
          refusedHeading="Not shown to you"
        />
      ) : null}

      {open && dependents.data ? (
        <>
          {lineageCaveat(dependents.data.lineageVersions) ? (
            <p className="rn-muted">{lineageCaveat(dependents.data.lineageVersions)}</p>
          ) : null}

          <Facet
            facet={dependents.data.thisVersion}
            scope="version"
            heading={
              dependents.data.lineageVersions > 1
                ? 'Decisions that rest on this version'
                : 'Decisions that rest on this rule'
            }
          />

          {/* Only when the two questions actually differ. */}
          {dependents.data.lineageVersions > 1 ? (
            <Facet
              facet={dependents.data.acrossVersions}
              scope="policy"
              heading="Decisions that rest on any version of this rule"
            />
          ) : null}
        </>
      ) : null}

      {/* ------------------------------------------------------------------
          THE ABSENCE THAT IS STILL TRUE. Kept, deliberately, under every
          branch above including the refusal — because it is a fact about the
          PRODUCT, not about this reader or this rule, and a reader who sees
          only the number would take it for a total.
         ------------------------------------------------------------------ */}
      <p className="rn-muted" data-untracked-consumers>
        Recommendations are the only thing Phibian records as having used a rule. Automations,
        forms, eligibility holds and tasks may rely on this rule as well, and none of them writes
        that down — so for those, this is still “not tracked” rather than “none”.
      </p>
    </section>
  );
}

function Facet({
  facet,
  scope,
  heading,
}: {
  facet: Facet;
  scope: DependentsScope;
  heading: string;
}) {
  const summary = dependentsSummary(facet, scope);
  return (
    <div className="rn-col" data-facet={scope} data-facet-kind={summary.kind}>
      <h3 className="rn-card-title">{heading}</h3>
      <p className="rn-strong">{summary.label}</p>
      <p className="rn-muted">{summary.detail}</p>
      {facet.state === 'tracked' && facet.dependents.length > 0 ? (
        <ul className="rn-list">
          {facet.dependents.map((dependent) => (
            <li key={dependent.recommendationId}>
              <span className="rn-strong">{dependent.headline}</span>
              <p className="rn-muted">
                {/* THE VERSION THIS DECISION ACTUALLY CITED, always, even on the
                    single-version answer. It is the fact that makes the record
                    evidence of a moment rather than a claim about today. */}
                {dependent.citedVersions
                  .map((citation) =>
                    citation.version === null
                      ? `version ${citation.versionLabel ?? 'unnumbered'}`
                      : `version ${citation.version}`,
                  )
                  .join(', ')}{' '}
                · asked about {dependent.citedVersions[0]?.asOfDate ?? 'an unrecorded day'} ·{' '}
                {dependent.citedVersions[0]?.disposition === 'excluded'
                  ? 'the rule was displaced by a more specific one'
                  : `the rule was ${dependent.citedVersions[0]?.role ?? 'cited'}`}
              </p>
            </li>
          ))}
        </ul>
      ) : null}
      {facet.state === 'tracked' && facet.windowExhausted ? (
        <p className="rn-muted">
          Phibian looked at the {facet.scanned} most recent citations and stopped there. There may
          be older ones this answer does not cover.
        </p>
      ) : null}
    </div>
  );
}
